import { useEffect, useRef } from 'react';
import {
  CandlestickSeries,
  LineSeries,
  createChart,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { Candle, Divergence, RsiPoint } from '@pipyar/core';
import { RSI_OVERBOUGHT, RSI_OVERSOLD } from '@pipyar/core';
import { chartPalette } from './chartTheme';
import { fitWithShift } from './chartShift';
import { cn } from '@/lib/utils';
import { useLiveBar } from '@/lib/useLiveBar';

interface RsiChartProps {
  candles: Candle[];
  points: RsiPoint[];
  divergences: Divergence[];
  digits: number;
  theme: 'dark' | 'light';
  resetKey?: string;
  className?: string;
}

/**
 * Price above, RSI below, in two panes of a single chart.
 *
 * One chart with two panes rather than two stacked charts: panes share the time
 * scale natively, so pan and zoom stay locked together. Two charts would need
 * their visible ranges synchronised by hand, which drifts under fast scrolling
 * and is a classic source of "the divergence line points at the wrong candle".
 */
export function RsiChart({
  candles,
  points,
  divergences,
  digits,
  theme,
  resetKey,
  className,
}: RsiChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const priceRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  // Last candle follows the pushed price between refetches. The RSI line
  // itself waits for the refetch: it is computed on the server from closes.
  useLiveBar(priceRef, candles);
  const rsiRef = useRef<ISeriesApi<'Line'> | null>(null);
  const overlayRef = useRef<ISeriesApi<'Line'>[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const palette = chartPalette(theme);

    const chart = createChart(container, {
      autoSize: true,
      layout: {
        background: { color: palette.background },
        textColor: palette.text,
        attributionLogo: false,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        panes: { separatorColor: palette.border, separatorHoverColor: palette.crosshair },
      },
      grid: {
        vertLines: { color: palette.grid },
        horzLines: { color: palette.grid },
      },
      rightPriceScale: { borderColor: palette.border },
      timeScale: {
        borderColor: palette.border,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 6,
      },
      crosshair: {
        /*
         * Normal, not the library's default of Magnet.
         *
         * Magnet snaps the price readout to the nearest OHLC of the bar under
         * the pointer, so the crosshair jumps to an open or close instead of
         * following the mouse. That is useful for reading a candle and actively
         * wrong for measuring anything between them — judging where a knot sits
         * inside a body, or how far price is from a gap edge.
         */
        mode: CrosshairMode.Normal,
        vertLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
        horzLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
      },
      localization: {
        // Times are on the broker's stored clock, so format as UTC — the
        // browser's zone would shift every label off the clock being traded.
        timeFormatter: (t: Time) => formatStoredTime(t as UTCTimestamp),
      },
    });

    const price = chart.addSeries(CandlestickSeries, {
      upColor: palette.bull,
      downColor: palette.bear,
      borderUpColor: palette.bull,
      borderDownColor: palette.bear,
      wickUpColor: palette.bull,
      wickDownColor: palette.bear,
      priceFormat: { type: 'price', precision: digits, minMove: 10 ** -digits },
    });

    // paneIndex 1 puts RSI in its own pane with its own 0-100 scale, which is
    // what stops a 0-100 oscillator from flattening a 4000-price chart.
    const rsi = chart.addSeries(
      LineSeries,
      {
        color: palette.rsi,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
        priceFormat: { type: 'price', precision: 1, minMove: 0.1 },
      },
      1,
    );

    // Reference levels drawn as price lines: they span the pane and never need
    // data points, so they stay put when the series is replaced.
    for (const [value, color] of [
      [RSI_OVERBOUGHT, palette.bear],
      [50, palette.text],
      [RSI_OVERSOLD, palette.bull],
    ] as const) {
      rsi.createPriceLine({
        price: value,
        color,
        lineWidth: 1,
        lineStyle: value === 50 ? 3 : 2,
        axisLabelVisible: true,
        title: String(value),
      });
    }

    chart.panes()[1]?.setHeight(150);

    chartRef.current = chart;
    priceRef.current = price;
    rsiRef.current = rsi;

    return () => {
      chart.remove();
      chartRef.current = null;
      priceRef.current = null;
      rsiRef.current = null;
      overlayRef.current = [];
    };
  }, [theme, digits]);

  // ---- data ----
  useEffect(() => {
    priceRef.current?.setData(
      candles.map((c) => ({
        time: c.time as UTCTimestamp,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })),
    );
  }, [candles]);

  useEffect(() => {
    rsiRef.current?.setData(
      points.map((p) => ({ time: p.time as UTCTimestamp, value: p.value })),
    );
  }, [points]);

  // ---- divergence lines, drawn on BOTH panes ----
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    for (const s of overlayRef.current) {
      try {
        chart.removeSeries(s);
      } catch {
        /* chart disposed */
      }
    }
    overlayRef.current = [];

    if (divergences.length === 0) return;

    const palette = chartPalette(theme);

    for (const d of divergences) {
      const color = d.bias === 'bullish' ? palette.bull : palette.bear;
      // Hidden divergences are continuation hints rather than reversal signals,
      // so they are dashed and thinner — visibly secondary to the regular ones.
      const dashed = d.kind === 'hidden';

      // On the price pane, connecting the two swing points.
      const priceLine = chart.addSeries(
        LineSeries,
        {
          color,
          lineWidth: dashed ? 1 : 2,
          lineStyle: dashed ? 2 : 0,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          priceFormat: { type: 'price', precision: digits, minMove: 10 ** -digits },
        },
        0,
      );
      priceLine.setData([
        { time: d.fromTime as UTCTimestamp, value: d.fromPrice },
        { time: d.toTime as UTCTimestamp, value: d.toPrice },
      ]);
      overlayRef.current.push(priceLine);

      // And the matching line on the RSI pane — the divergence only means
      // anything when both halves are visible together.
      const rsiLine = chart.addSeries(
        LineSeries,
        {
          color,
          lineWidth: dashed ? 1 : 2,
          lineStyle: dashed ? 2 : 0,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          priceFormat: { type: 'price', precision: 1, minMove: 0.1 },
        },
        1,
      );
      rsiLine.setData([
        { time: d.fromTime as UTCTimestamp, value: d.fromRsi },
        { time: d.toTime as UTCTimestamp, value: d.toRsi },
      ]);
      overlayRef.current.push(rsiLine);
    }
  }, [divergences, theme, digits]);

  // Fit the series with chart shift: see chartShift.ts.
  useEffect(() => {
    const ts = chartRef.current?.timeScale();
    if (ts) fitWithShift(ts);
  }, [resetKey]);

  return <div ref={containerRef} className={cn('w-full', className)} />;
}

function formatStoredTime(t: UTCTimestamp): string {
  const d = new Date((t as number) * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(
    d.getUTCHours(),
  )}:${pad(d.getUTCMinutes())}`;
}
